import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, Stack, Tabs, Text} from '@mantine/core';
import React from 'react';

import ChatSettingsListItem from 'llm/chat-library/shared/list-detail/ChatSettingsListItem';
import {ListGroup, ListingViewHeader} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  header: {
    padding: `${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.none}`,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsList: {
    padding: `${theme.spacing.none} ${theme.spacing.md}`,
  },
  tab: {
    padding: theme.spacing.md,
    '&[data-active]': {
      borderBottomWidth: 3,
    },
  },
  scroll: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing.md,
  },
  empty: {
    padding: theme.spacing.xl,
    textAlign: 'center',
  },
}));

export function ChatSettingsListingHeader({header}: {header: ListingViewHeader}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack spacing="xs" className={classes.header}>
      <Group noWrap={true} position="apart" align="center">
        <Text fw="bold" size="xl">
          {header.title}
        </Text>
        {header.action}
      </Group>
    </Stack>
  );
}

export default function ChatSettingsListingView({
  header,
  groups,
  activeGroupId,
  onGroupChange,
  isLoading = false,
  emptyMessage = 'Nothing to show yet.',
  selectedItemId,
  onSelectItem,
}: {
  header?: ListingViewHeader;
  groups: Array<ListGroup>;
  /** The selected group's tab. Defaults to the first group when unset. */
  activeGroupId?: string;
  onGroupChange?: (groupId: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  selectedItemId?: string | null;
  onSelectItem: (id: string) => void;
}): JSX.Element {
  const {classes} = useStyles();

  // Groups always render as a horizontal tab bar; fall back to the first group so a tab is always
  // active even when the caller doesn't control the selection.
  const selectedGroupId = activeGroupId ?? groups[0]?.id;
  const activeGroup = groups.find((group) => group.id === selectedGroupId);

  return (
    <div className={classes.root}>
      {header != null && <ChatSettingsListingHeader header={header} />}
      <Tabs
        value={selectedGroupId}
        onTabChange={(value) => {
          if (value != null) {
            onGroupChange?.(value);
          }
        }}
        keepMounted={false}
        className={classes.tabsRoot}
      >
        <Tabs.List className={classes.tabsList}>
          {groups.map((group, index) => (
            <Tabs.Tab
              key={group.id ?? index}
              value={group.id ?? ''}
              className={classes.tab}
              {...getTestProps({component: 'ChatSettingsListingView', element: 'tab', key: group.id})}
            >
              {group.header}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value={selectedGroupId ?? ''} className={classes.tabsRoot}>
          {isLoading ? (
            <CenteredSpinner />
          ) : activeGroup == null || activeGroup.items.length === 0 ? (
            <Text className={classes.empty} c="dimmed">
              {emptyMessage}
            </Text>
          ) : (
            <Stack spacing="lg" className={classes.scroll}>
              <Stack spacing="md">
                {activeGroup.items.map((item) => (
                  <ChatSettingsListItem
                    key={item.id}
                    item={item}
                    selected={item.id === selectedItemId}
                    onClick={() => {
                      onSelectItem(item.id);
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
